export const FEATURE_FLAG_KEYS = {
  mspHeatmap: 'msp_heatmap',
  vendorProfileChecklist: 'vendor_profile_checklist',
  personalAccessTokensUi: 'personal_access_tokens_ui',
  adminActionDashboard: 'admin_action_dashboard',
  hideSecurityChecksAccordion: 'hide_security_checks_accordion',
  vendorQuestionnaireScrollNav: 'vendor_questionnaire_scroll_nav',
  vendorExternalShareConsumption: 'vendor_external_share_consumption',
  breakglassAccess: 'breakglass_access',
  customerQuestionnaireLegacyView: 'customer_questionnaire_legacy_view',
  inAppSecurityDocs: 'enable_inapp_security_docs',
  navigatorSupplyChainBriefing: 'navigator_supply_chain_briefing',
  navigatorContractsRealData: 'navigator_contracts_real_data',
  manualBreachNotifications: 'manual_breach_notifications',
  supplierDocumentAnalysis: 'supplier_document_analysis',
  insuranceCertificates: 'insurance_certificates',
  breachAwareTraining: 'breach_aware_training',
  breachIntelligenceV2Live: 'breach_intelligence_v2_live',
  pitchBookBriefing: 'pitch_book_briefing',
  modernToast: 'modern_toast',
  marketplaceConsoleLegacyView: 'marketplace_console_legacy_view',
  questionnaireInsights: 'questionnaire_insights',
  scanEngineSettings: 'scan_engine_settings',
  scanEngineOperations: 'scan_engine_operations',
  canonicalLogoProcessing: 'canonical_logo_processing',
  passkeyControlPlane: 'passkey_control_plane',
  enterpriseSso: 'enterprise_sso',
  supplySteward: 'supply_steward',
  registrarDnsBaselineEvidence: 'registrar_dns_baseline_evidence',
  atlasFindingsView: 'atlas_findings_view',
  runtimeAdvisories: 'runtime_advisories',
  supplierRegisterReport: 'supplier_register_report',
  supplierProfileStewardship: 'supplier_profile_stewardship',
  mailGatewayDenylist: 'mail_gateway_denylist',
  customQuestionnairePicker: 'custom_questionnaire_picker',
} as const;

export type KnownFeatureFlagKey = typeof FEATURE_FLAG_KEYS[keyof typeof FEATURE_FLAG_KEYS];

export type FeatureFlagCatalogEntry = {
  key: KnownFeatureFlagKey;
  name: string;
  description: string;
  surfaces: string[];
  linearIssueUrl: string;
  allowedTargetingModes?: readonly FeatureFlagTargetingMode[];
};

export const FEATURE_FLAG_TARGETING_MODES = {
  allUsers: 'all_users',
  specificUsers: 'specific_users',
  specificMarketplaceListings: 'specific_marketplace_listings',
  specificOrganizations: 'specific_organizations',
  allOrganizations: 'all_organizations',
} as const;

export type FeatureFlagTargetingMode =
  typeof FEATURE_FLAG_TARGETING_MODES[keyof typeof FEATURE_FLAG_TARGETING_MODES];

export const DEFAULT_FEATURE_FLAG_TARGETING_MODES = [
  FEATURE_FLAG_TARGETING_MODES.allUsers,
  FEATURE_FLAG_TARGETING_MODES.specificUsers,
] as const satisfies readonly FeatureFlagTargetingMode[];

export const FEATURE_FLAG_CATALOG: readonly FeatureFlagCatalogEntry[] = [
  {
    key: FEATURE_FLAG_KEYS.mailGatewayDenylist,
    name: 'Mail Gateway Denylist',
    description:
      'Controls organisation access to Reports → Mail gateway denylist: the downloadable list of supplier domains that send no email and lack DMARC reject, plus the enforce list (ENT-5550).',
    surfaces: ['/reports?tab=denylist', 'My Suppliers › Supplier card denylist notice'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-6438',
    allowedTargetingModes: [
      FEATURE_FLAG_TARGETING_MODES.specificOrganizations,
      FEATURE_FLAG_TARGETING_MODES.allOrganizations,
    ],
  },
  {
    key: FEATURE_FLAG_KEYS.customQuestionnairePicker,
    name: 'Custom Questionnaire Picker',
    description:
      'Controls organisation access to customer-owned questionnaires assembled from canonical platform questions. Turning it off hides the feature and keeps the organisation\'s questionnaires.',
    surfaces: ['/questionnaires/custom', '/insights?tab=questionnaires', 'customer_sidebar', 'supplier-questionnaire-requests'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-6438',
    allowedTargetingModes: [
      FEATURE_FLAG_TARGETING_MODES.specificOrganizations,
      FEATURE_FLAG_TARGETING_MODES.allOrganizations,
    ],
  },
  {
    key: FEATURE_FLAG_KEYS.supplierProfileStewardship,
    name: 'Supplier profile stewardship',
    description:
      'Supply Guard Managed state, public-sources disclaimers, stewardship claim requests and the admin Stewardship column (ENT-6257).',
    surfaces: [
      'My Suppliers › Supplier profile',
      'Marketplace',
      'Claim Supplier Profile',
      'Vendor profile',
      'Admin › Marketplace › Listings',
    ],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-6257',
    allowedTargetingModes: [
      FEATURE_FLAG_TARGETING_MODES.specificOrganizations,
      FEATURE_FLAG_TARGETING_MODES.allOrganizations,
    ],
  },
  {
    key: FEATURE_FLAG_KEYS.supplierRegisterReport,
    name: 'Supplier register',
    description: 'Controls the organisation rollout of the Supplier register report, its export, and supplier data-types editing path.',
    surfaces: ['/reports?tab=supplier-register', '/suppliers/:id?peekTab=overview', 'supplier-register-report'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-5776',
    allowedTargetingModes: [
      FEATURE_FLAG_TARGETING_MODES.specificOrganizations,
      FEATURE_FLAG_TARGETING_MODES.allOrganizations,
    ],
  },
  {
    key: FEATURE_FLAG_KEYS.mspHeatmap,
    name: 'MSP Heatmap',
    description: 'Controls the MSP heatmap page rollout.',
    surfaces: ['/msp-heatmap', 'msp_sidebar'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-426',
  },
  {
    key: FEATURE_FLAG_KEYS.vendorProfileChecklist,
    name: 'Vendor Profile Checklist',
    description: 'Replaces the profile completeness stat card with an actionable checklist banner on the vendor dashboard.',
    surfaces: ['/vendor'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-553',
  },
  {
    key: FEATURE_FLAG_KEYS.personalAccessTokensUi,
    name: 'Personal Access Tokens UI',
    description: 'Controls the developer integrations beta UI for personal access tokens, supplier API docs, and webhook testing.',
    surfaces: ['/settings?tab=integrations', '/docs'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-696',
  },
  {
    key: FEATURE_FLAG_KEYS.adminActionDashboard,
    name: 'Admin Action Dashboard',
    description:
      'Replaces the legacy /admin overview with an action-only operational inbox that aggregates billing, cron, queue, API budget, and email issues for super admins.',
    surfaces: ['/admin'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-713',
  },
  {
    key: FEATURE_FLAG_KEYS.hideSecurityChecksAccordion,
    name: 'Marketplace Top-Level SupplierLens Only',
    description:
      'Hides component-level SupplierLens/security check accordions on marketplace listing detail, supplier detail, and PitchBook security pages, leaving only the top-level SupplierLens score visible.',
    surfaces: ['/marketplace', '/suppliers/:id', '/pitchbook'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-749',
  },
  {
    key: FEATURE_FLAG_KEYS.vendorQuestionnaireScrollNav,
    name: 'Vendor Questionnaire Scroll Navigation',
    description: 'Enables the scroll-linked questionnaire detail UI where section pills jump to anchors and scrolling updates the active pill.',
    surfaces: ['/vendor/questionnaires/:slug'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-735',
  },
  {
    key: FEATURE_FLAG_KEYS.vendorExternalShareConsumption,
    name: 'Vendor External Share Consumption',
    description:
      'Shows vendors which approved external questionnaire viewers consumed a magic link and when.',
    surfaces: ['/vendor/visibility?tab=settings'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-830',
  },
  {
    key: FEATURE_FLAG_KEYS.breakglassAccess,
    name: 'Break Glass Access',
    description:
      'Controls the break glass temporary org access feature. When enabled, super admins can request time-limited access to any org on behalf of a user.',
    surfaces: ['/admin?tab=accounts&subtab=breakglass', '/settings?tab=breakglass'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-744',
  },
  {
    key: FEATURE_FLAG_KEYS.customerQuestionnaireLegacyView,
    name: 'Customer Questionnaire Legacy View',
    description: 'Switches the supplier questionnaires tab back to the legacy individual cards layout instead of the aggregate portfolio review.',
    surfaces: ['/suppliers/:id?tab=questionnaires'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-742',
  },
  {
    key: FEATURE_FLAG_KEYS.inAppSecurityDocs,
    name: 'In-app Security Documentation',
    description:
      'Renders the customer/MSP-facing security documentation panel under Settings → Security so admins can read how authentication, MFA, sessions, and break-glass work and copy compliance crosswalk citations into their own questionnaires.',
    surfaces: ['/settings?tab=security'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-1277',
  },
  {
    key: FEATURE_FLAG_KEYS.navigatorSupplyChainBriefing,
    name: 'Navigator Supply Chain Briefing',
    description:
      'Controls the Navigator → Supply Chain Briefing rollout. When disabled, the entry pill on /navigator, the /navigator/supply-chain-briefing route, and the Master Admin → Supply Chain Briefing library management tab are all hidden.',
    surfaces: ['/navigator', '/navigator/supply-chain-briefing', '/admin?tab=platform&subtab=supply-chain-briefing'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-1259',
  },
  {
    key: FEATURE_FLAG_KEYS.navigatorContractsRealData,
    name: 'Navigator Contracts Real Data',
    description:
      'Controls whether the Supply Chain Briefing Contracts pillar derives its status from real navigator_programme_contract_status rows (per-supplier contract tracking) or from the legacy questionnaire/certification coverage heuristic. When disabled, the briefing falls back to the heuristic with no behavioural change. When enabled per org, the pillar shows truthful coverage like "3 of 5 important suppliers have a current contract on file".',
    surfaces: ['/navigator/supply-chain-briefing'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-1675',
  },
  {
    key: FEATURE_FLAG_KEYS.manualBreachNotifications,
    name: 'Manual Breach Notifications',
    description:
      'Controls the manual supplier breach notification workflow: supplier case logging, customer receiver ownership, platform notifications, and customer acknowledgements.',
    surfaces: ['/breach-notifications', '/vendor/breach-notifications'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-1705',
  },
  {
    key: FEATURE_FLAG_KEYS.supplierDocumentAnalysis,
    name: 'Supplier Document Analysis',
    description:
      'Controls supplier-uploaded SOC 2 analysis, customer-local analyzed supplier evidence uploads, and the customer-facing analyzed-evidence rendering on My Suppliers > Supplier > Documents.',
    surfaces: ['/vendor', '/vendor/certifications', '/suppliers/:id?tab=documents', '/insights'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-2237',
  },
  {
    key: FEATURE_FLAG_KEYS.insuranceCertificates,
    name: 'Insurance Certificates',
    description:
      'Controls the insurance certificate capability across customer Insights, supplier/customer document workflows, supplier canonical insurance uploads, and the related insurance sharing access surfaces.',
    surfaces: ['/insights?tab=insurance', '/suppliers/:id?tab=documents', '/vendor/certifications?tab=insurance', '/vendor/visibility'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-785',
  },
  {
    key: FEATURE_FLAG_KEYS.breachAwareTraining,
    name: 'Breach Aware Assessment',
    description:
      'Controls the supplier Breach Aware assessment, completion records, and certification badge.',
    surfaces: ['/vendor/breach-notifications', '/vendor/certifications'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-2474',
  },
  {
    key: FEATURE_FLAG_KEYS.breachIntelligenceV2Live,
    name: 'Breach Intelligence v2 live',
    description:
      'The single shadow→live cutover for Breach Intelligence v2. When OFF (default) the v2 composite is shadow-only — persisted but the canonical breach-history score stays on v1 and no attribution renders. When ON, the v2 composite becomes the canonical breach-history score and the supplier score surface shows source attribution + plain-language explanations of the breach intelligence. Resolved by the canonical writer (capture-score-snapshots) and stamped per snapshot; the customer UI gates attribution on that per-snapshot stamp.',
    surfaces: ['/suppliers/:id', '/marketplace'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-1826',
  },
  {
    key: FEATURE_FLAG_KEYS.pitchBookBriefing,
    name: 'Pitch Book Prospect Briefing',
    description:
      'Controls the Pitch Book prospect briefing one-pager rollout. When disabled, the per-row "Briefing" action on /pitch-book is hidden and the /pitch-book/briefing/:domain route fails closed (redirects to /pitch-book). When enabled, MSP admins can open, print, and share a co-branded prospect security briefing.',
    surfaces: ['/pitch-book', '/pitch-book/briefing/:domain'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-1882',
  },
  {
    key: FEATURE_FLAG_KEYS.modernToast,
    name: 'Modern Toast Notifications',
    description:
      'Rollout kill-switch for the modernised toast notifications (ENT-1956). Default ON (seeded enabled for all users) — the whole app renders the new toast whose finish (Aurora glass / Accent rail + translucency) is chosen platform-wide in Admin → Platform → Security. When a super admin turns this OFF, every toast reverts to the pre-redesign legacy look. Presentation-only; no data or entitlement gating.',
    surfaces: ['global (app-wide toast surface)'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-1956',
  },
  {
    key: FEATURE_FLAG_KEYS.marketplaceConsoleLegacyView,
    name: 'Marketplace Console Legacy View',
    description:
      'Reverts the super-admin Marketplace listings view to the pre-ENT-2307 card layout (A-Z alphabet filter + stacked action cards). The new canonical DataTable console (in-toolbar search/filters, resizable/reorderable columns with memory, frozen columns, keyboard nav) is ON BY DEFAULT; enable this flag ONLY to fall back to the legacy layout. All data, row/bulk actions, dialogs, and the other marketplace sub-views are identical between the two layouts - this switches presentation only. Not a data-access control.',
    surfaces: ['/admin?tab=marketplace&subtab=listings'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-2307',
  },
  {
    key: FEATURE_FLAG_KEYS.questionnaireInsights,
    name: 'Questionnaire Insights',
    description:
      'Controls the Insights -> Questionnaires rollout, including the questionnaire health overview, questionnaire type scorecards, aggregate supplier-access table, and questionnaire-specific supplier breakdown flows.',
    surfaces: ['/insights?tab=questionnaires'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-2238',
  },
  {
    key: FEATURE_FLAG_KEYS.scanEngineSettings,
    name: 'Scan Engine Settings',
    description:
      'Admin > Scan Engine timeout settings tab (TLS scan probe deadline hierarchy). Default OFF / fail-closed: when disabled, the Scan Engine nav item, the admin-scan-engine command palette entry, and the /admin?tab=scan-engine tab content are all hidden and a direct ?tab=scan-engine hit redirects to the admin overview once the flag resolves.',
    surfaces: ['/admin?tab=scan-engine'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-2722',
  },
  {
    key: FEATURE_FLAG_KEYS.scanEngineOperations,
    name: 'Fleet Operations',
    description:
      'Admin > Fleet Operations tab (platform worker fleets, formerly "Scan Engine Operations": live scan queue breakdown, historical aggregate snapshots, the executor-scoped drain forecast, and the rollout / scale / runtime-policy controls). Default OFF / fail-closed: when disabled, the nav item, the command palette entry, and the /admin?tab=fleet-operations tab content are all hidden and a direct hit (including the legacy tab slug) redirects to the admin overview once the flag resolves. The flag key keeps its original scan_engine_operations value (ENT-5191).',
    surfaces: ['/admin?tab=fleet-operations'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-2993',
  },
  {
    key: FEATURE_FLAG_KEYS.canonicalLogoProcessing,
    name: 'Canonical Logo Processing',
    description:
      'Controls the super-admin Logo Processing panel for queue visibility and the server-owned bounded attempts setting. The Edge boundary independently enforces super_admin authorization.',
    surfaces: ['/admin?tab=logo-processing'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-6154',
  },
  {
    key: FEATURE_FLAG_KEYS.passkeyControlPlane,
    name: 'Passkey Control Plane',
    description:
      'Exposure control for the per-super-admin passkey (WebAuthn MFA factor) access grant (ENT-3028 Path B). Default OFF / fail-closed: when disabled, the Passkey state column and the Fingerprint enable/disable action in Admin → Accounts → Users are hidden, and the admin-passkey-access edge function rejects with 403 feature_not_enabled. This is exposure control only — the edge function and RPC still enforce super_admin + fresh-AAL2 independently. Enrollment and sign-in enforcement land in PR2.',
    surfaces: ['/admin?tab=accounts&subtab=users'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-3028',
  },
  {
    key: FEATURE_FLAG_KEYS.enterpriseSso,
    name: 'Enterprise SSO',
    description: 'Controls Enterprise SSO rollout at the organisation level. It is not a commercial entitlement: eligible members of a targeted organisation (or of every organisation, once rolled out to all) can use the SSO surfaces and trusted SSO paths; non-targeted organisations remain dark.',
    surfaces: ['/settings?tab=security', '/access-pending', 'org-sso-admin', 'sso-claim-membership'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-4214',
    allowedTargetingModes: [
      FEATURE_FLAG_TARGETING_MODES.specificOrganizations,
      FEATURE_FLAG_TARGETING_MODES.allOrganizations,
    ],
  },
  {
    key: FEATURE_FLAG_KEYS.supplySteward,
    name: 'SupplySteward',
    description:
      'Supply Guard\'s per-organisation switch for the SupplySteward assistant (ENT-5511). Off by default. Only a targeted organisation (or every organisation, once rolled out to all) sees the SupplySteward settings tab and chat button, and only its own Settings → SupplySteward opt-in can then turn the assistant on. When off, ai-agent-gateway refuses with 403 feature_not_enabled and agent-fab-read-model hides the chat button, whatever the organisation\'s own setting.',
    surfaces: ['/settings/organisation?tab=ai-agent', 'agent-fab-read-model', 'ai-agent-gateway', '/admin?tab=ai-agent'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-5511',
    allowedTargetingModes: [
      FEATURE_FLAG_TARGETING_MODES.specificOrganizations,
      FEATURE_FLAG_TARGETING_MODES.allOrganizations,
    ],
  },
  {
    key: FEATURE_FLAG_KEYS.registrarDnsBaselineEvidence,
    name: 'Registrar scan: DNS-baseline evidence',
    description:
      'Registrar scans derive DNSSEC/NS/CAA from DNS and treat APIvoid as best-effort enrichment (ENT-4360). Target listings first, then all.',
    surfaces: ['scan-domain-registrar'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-4360',
    allowedTargetingModes: [
      FEATURE_FLAG_TARGETING_MODES.allUsers,
      FEATURE_FLAG_TARGETING_MODES.specificMarketplaceListings,
    ],
  },
  {
    key: FEATURE_FLAG_KEYS.atlasFindingsView,
    name: 'Atlas findings view',
    description:
      'Findings-first Vulnerabilities tab in the Security Atlas with the super-admin Hints rail',
    surfaces: ['Security Atlas › Vulnerabilities tab', 'Top bar (super admins)'],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-4390',
  },
  {
    key: FEATURE_FLAG_KEYS.runtimeAdvisories,
    name: 'Runtime advisories',
    description:
      'Per-organisation exposure switch for runtime advisories (ENT-5889, php2shell and later): the Advisories nav item and pages, the MSP and supplier advisory views, the Atlas runtime findings, the Infrastructure runtime chips, the runtime_advisory alert and advisory emails. Off by default; a non-targeted organisation sees none of it and the console never emails it. The public /check page needs it on for all organisations. Technology rows and re-checks are collected regardless, so enabling needs no backfill. Exposure only: every RPC still enforces access independently.',
    surfaces: [
      '/advisories', '/advisories/:advisoryId', '/vendor/advisories', '/vendor/advisories/:advisoryId',
      '/check/:domain', 'Security Atlas runtime findings', 'Infrastructure tab runtime chips',
      'runtime_advisory alerts', '/admin/advisories/:advisoryId (send)',
    ],
    linearIssueUrl: 'https://linear.app/supply-guard/issue/ENT-5889',
    allowedTargetingModes: [
      FEATURE_FLAG_TARGETING_MODES.specificOrganizations,
      FEATURE_FLAG_TARGETING_MODES.allOrganizations,
    ],
  },
] as const;

export function getFeatureFlagCatalogEntry(key: string) {
  return FEATURE_FLAG_CATALOG.find((entry) => entry.key === key) ?? null;
}
